<template>
  <div class="container my-4">
    <!-- Loader pantalla completa mientras se genera la factura FEL -->
    <div
      v-if="(cargando && generarFactura) || cargandoPagoCombinado || esperandoImpresion"
      style="
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.72);
        z-index: 9999;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
      "
    >
      <div
        class="spinner-border text-light"
        style="width: 3.5rem; height: 3.5rem"
      ></div>
      <div class="text-white fw-semibold fs-5">
        Generando factura electrónica...
      </div>
      <div class="text-white-50 small">
        Por favor espere, procesando con la SAT
      </div>
    </div>
    <div v-if="cuenta" class="card shadow-sm p-4">
      <!-- Header -->
      <div class="row align-items-center mb-3">
        <div class="col-auto">
          <router-link
            to="/cuentas-pendientes-mesas"
            class="btn btn-outline-secondary btn-sm"
          >
            ⬅ Volver
          </router-link>
        </div>
        <div class="col text-center">
          <h5 class="fw-semibold text-primary mb-0">
            💵 Pago de {{ cuenta.NombreCuenta }} - Pedido {{ cuenta.IdPedido }}
          </h5>
        </div>
        <div class="col-auto"></div>
      </div>

      <!-- Info general -->
      <div class="mb-4">
        <!-- Fila 1: Identificación -->
        <div class="row mb-3 pb-3 border-bottom">
          <div class="col-md-4">
            <small class="text-muted d-block">Mesa</small>
            <span class="fw-semibold">{{ mesa.Numero }}</span>
          </div>
          <div class="col-md-4">
            <small class="text-muted d-block">Pedido</small>
            <span class="fw-semibold">#{{ cuenta.IdPedido }}</span>
          </div>
          <div class="col-md-4">
            <small class="text-muted d-block">Estado</small>
            <span :class="estadoBadgeClass">
              {{ estadoTexto }}
            </span>
          </div>
        </div>

        <!-- Fila 2: Montos -->
        <div class="row mb-3 pb-3 border-bottom">
          <div class="col-md-3">
            <small class="text-muted d-block">Subtotal</small>
            <span class="fw-semibold"
              >Q{{ parseFloat(cuenta.Subtotal).toFixed(2) }}</span
            >
          </div>
          <div class="col-md-3">
            <small class="text-muted d-block">Total</small>
            <span class="fw-bold text-primary"
              >Q{{
                parseFloat(cuenta.TotalFinal ?? cuenta.Total).toFixed(2)
              }}</span
            >
          </div>
          <div class="col-md-3">
            <small class="text-muted d-block">Saldo Pendiente</small>
            <span class="fw-bold text-danger"
              >Q{{
                parseFloat(
                  cuenta.SaldoPendienteCuenta ??
                    cuenta.TotalFinal ??
                    cuenta.Total,
                ).toFixed(2)
              }}</span
            >
          </div>
        </div>

        <!-- Fila 3: Propina -->
        <div class="row align-items-center">
          <div class="col-12">
            <small class="text-muted d-block">Propina</small>
            <div class="d-flex align-items-center flex-wrap gap-2">
              <span class="fw-semibold"
                >Q{{ parseFloat(cuenta.Propina).toFixed(2) }}</span
              >
              <label class="switch mb-0">
                <input
                  type="checkbox"
                  v-model="propinaActiva"
                  @change="togglePropina"
                  :disabled="cargando || cuenta.Estado !== 0"
                />
                <span class="slider"></span>
              </label>
              <span class="small">{{
                propinaActiva ? "Con propina" : "Sin propina"
              }}</span>
              <span v-if="cuenta.EstadoPropina === 1" class="badge bg-success"
                >Aplicada</span
              >
              <!-- Campo de monto personalizado de propina -->
              <div
                v-if="propinaActiva && cuenta.Estado === 0"
                class="d-flex align-items-center gap-1 ms-1"
              >
                <span class="small text-muted">Monto:</span>
                <div class="input-group input-group-sm" style="width: 130px">
                  <span class="input-group-text px-1">Q</span>
                  <input
                    type="number"
                    class="form-control form-control-sm"
                    v-model.number="propinaPersonalizada"
                    :placeholder="propinaAutoCalculada"
                    min="0.01"
                    step="0.01"
                    :disabled="cargando"
                    @blur="aplicarPropinaPersonalizada"
                    @keydown.enter.prevent="aplicarPropinaPersonalizada"
                    title="Dejar vacío para usar el 10% automático"
                  />
                </div>
                <button
                  v-if="
                    propinaPersonalizada !== null && propinaPersonalizada !== ''
                  "
                  class="btn btn-sm btn-outline-secondary py-0 px-1"
                  @click="limpiarPropinaPersonalizada"
                  :disabled="cargando"
                  title="Restablecer al 10% automático"
                >
                  <small>Auto</small>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Botón para abrir detalles -->
      <div class="text-center mb-4">
        <button
          class="btn btn-outline-primary btn-sm me-2"
          @click="abrirModalDetalles"
        >
          Ver Detalles de Consumo
        </button>
        <button
          class="btn btn-outline-info btn-sm me-2"
          @click="abrirModalPagos"
        >
          Ver Pagos Realizados
        </button>
        <button
          class="btn btn-outline-secondary btn-sm me-2"
          @click="abrirModalUnificaciones"
        >
          🔗 Ver Unificaciones
        </button>
        <button
          v-if="cuenta.Estado === 0 && cuenta.SaldoPendienteCuenta > 0"
          class="btn btn-outline-success btn-sm"
          @click="abrirModalDescuento"
        >
          Aplicar Código de Descuento
        </button>
      </div>

      <!-- Mostrar descuento aplicado -->
      <div
        v-if="cuenta.SaldoDescuento > 0"
        class="alert alert-success mb-4 d-flex justify-content-between align-items-center flex-wrap gap-2"
      >
        <span>
          <strong>💚 Descuento Aplicado:</strong> Q{{
            parseFloat(cuenta.SaldoDescuento).toFixed(2)
          }}
        </span>
        <button
          v-if="cuenta.Estado !== 2 && cuenta.Estado !== 3"
          class="btn btn-outline-danger btn-sm"
          @click="modalQuitarDescuentoVisible = true"
        >
          Quitar Descuento
        </button>
      </div>

      <!-- Modal Quitar Descuento -->
      <div
        v-if="modalQuitarDescuentoVisible"
        class="modal fade show d-block"
        tabindex="-1"
        style="background-color: rgba(0, 0, 0, 0.5)"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Quitar Descuento</h5>
              <button
                class="btn-close"
                @click="modalQuitarDescuentoVisible = false"
              ></button>
            </div>
            <div class="modal-body">
              <p>
                ¿Está seguro de quitar el descuento aplicado a esta cuenta? Se
                revertirá el siguiente monto al saldo pendiente:
              </p>
              <div class="alert alert-warning">
                <strong>Monto a revertir:</strong> Q{{
                  parseFloat(cuenta.SaldoDescuento || 0).toFixed(2)
                }}
              </div>
            </div>
            <div class="modal-footer">
              <button
                class="btn btn-secondary"
                @click="modalQuitarDescuentoVisible = false"
              >
                Cancelar
              </button>
              <button
                class="btn btn-danger"
                @click="confirmarQuitarDescuentoCuenta"
                :disabled="cargandoQuitarDescuento"
              >
                <span
                  v-if="cargandoQuitarDescuento"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                Quitar Descuento
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Tipo de pago (PRIMERO) - Solo si está pendiente -->
      <div v-if="cuenta.Estado === 0" class="mb-4">
        <label class="form-label fw-semibold">Tipo de pago</label>
        <select v-model="tipoPago" class="form-select">
          <option :value="null" disabled>Seleccione tipo de pago</option>
          <option value="pagar">Pagar ahora</option>
          <option value="cargar_habitacion">Cargar a habitación</option>
        </select>
      </div>

      <!-- Buscar reserva (solo si se carga a habitación) -->
      <div
        v-if="tipoPago === 'cargar_habitacion' && cuenta.Estado === 0"
        class="mb-4"
      >
        <label class="form-label">Buscar Reserva</label>

        <input
          v-model="busquedaReserva"
          class="form-control"
          placeholder="Código de reserva"
          @input="debouncedBuscarReservas"
        />

        <!-- Lista de reservas -->
        <ul
          class="list-group mt-2"
          v-if="Array.isArray(reservasFiltradas) && reservasFiltradas.length"
        >
          <li
            v-for="res in reservasFiltradas"
            :key="res.IdReserva"
            class="list-group-item"
          >
            <div class="d-flex flex-wrap align-items-center gap-2">
              <span class="fw-semibold">R00{{ res.IdReserva }}</span>
              <span>–</span>
              <span>{{ res.huesped?.Nombre }} {{ res.huesped?.Apellido }}</span>
              <span class="badge bg-secondary">{{ res.Estado }}</span>
              <span class="text-muted small"
                >📅 Fecha llegada: {{ formatearFecha(res.FechaLlegada) }}</span
              >
              <span class="text-muted small"
                >📅 Fecha fin: {{ formatearFecha(res.FechaSalida) }}</span
              >
            </div>

            <!-- Habitaciones de la reserva -->
            <ul
              v-if="
                Array.isArray(res.habitaciones_info) &&
                res.habitaciones_info.length
              "
              class="list-group mt-2 ms-3"
            >
              <li
                v-for="hab in res.habitaciones_info"
                :key="hab.IdReservaHabitacion"
                class="list-group-item list-group-item-action"
                @click="seleccionarHabitacion(res, hab)"
              >
                🛏️ Habitación {{ hab.Numero }}
              </li>
            </ul>
          </li>
        </ul>

        <!-- Mensaje cuando no se encontraron reservas -->
        <div
          v-if="
            busquedaReserva.length >= 2 &&
            Array.isArray(reservasFiltradas) &&
            reservasFiltradas.length === 0 &&
            !habitacionSeleccionada
          "
          class="alert alert-warning mt-2"
        >
          ⚠️ Reserva no encontrada
        </div>

        <!-- Habitación seleccionada -->
        <div v-if="habitacionSeleccionada" class="alert alert-info mt-3">
          🛏️ Se cargará a la habitación
          <strong>{{ habitacionSeleccionada.Numero }}</strong>
        </div>
      </div>

      <!-- Confirmar pago - Solo si está pendiente -->
      <div v-if="cuenta.Estado === 0" class="text-end mt-3">
        <button
          class="btn btn-success"
          @click="iniciarPago"
          :disabled="
            cargando ||
            !tipoPago ||
            (tipoPago === 'cargar_habitacion' && !habitacionSeleccionada)
          "
        >
          💳 Confirmar
        </button>
      </div>

      <!-- Cambiar habitación (cuando está cargada a una) -->
      <div v-if="cuenta.Estado === 1" class="alert alert-info mt-3">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            🛏️ Esta cuenta está cargada a una habitación
          </div>
          <button class="btn btn-sm btn-outline-primary" @click="abrirModalCambiarHabitacion">
            🔄 Cambiar Habitación
          </button>
        </div>
      </div>
      <div
        v-if="cuenta.Estado === 2"
        class="alert alert-success text-center mt-3"
      >
        ✅ Esta cuenta ya ha sido pagada
      </div>
    </div>

    <!-- Loader -->
    <div v-else class="text-center text-muted py-5">
      <div class="spinner-border text-primary mb-3" role="status"></div>
      <p>Cargando detalles de la cuenta...</p>
    </div>

    <!-- Modal Detalles -->
    <div
      v-if="mostrarModalDetalles"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">🧾 Detalles de la Cuenta</h5>
            <button class="btn-close" @click="cerrarModalDetalles"></button>
          </div>
          <div class="modal-body">
            <div v-if="detalles.length" class="table-responsive">
              <table class="table table-bordered align-middle">
                <thead class="bg-light">
                  <tr>
                    <th class="text-start">Descripción</th>
                    <th>Cantidad</th>
                    <th>Precio Unit. (Q)</th>
                    <th>Subtotal (Q)</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(item, i) in detalles" :key="i">
                    <!-- Fila del platillo -->
                    <tr>
                      <td class="text-start">
                        <strong>{{ item.nombre_platillo }}</strong>
                      </td>
                      <td>{{ item.cantidad }}</td>
                      <td>
                        Q{{ parseFloat(item.precio_unitario).toFixed(2) }}
                      </td>
                      <td>
                        Q{{
                          (
                            parseFloat(item.precio_unitario) * item.cantidad
                          ).toFixed(2)
                        }}
                      </td>
                    </tr>
                    <!-- Filas de opciones/extras -->
                    <tr
                      v-for="opcion in item.opciones"
                      :key="'op-' + i + '-' + opcion.IdOpcion"
                      class="table-light"
                    >
                      <td class="text-start ps-4">
                        <small class="text-muted">↳</small> {{ opcion.nombre }}
                      </td>
                      <td>{{ opcion.cantidad }}</td>
                      <td>Q{{ parseFloat(opcion.precio_extra).toFixed(2) }}</td>
                      <td>
                        Q{{
                          (
                            parseFloat(opcion.precio_extra) * opcion.cantidad
                          ).toFixed(2)
                        }}
                      </td>
                    </tr>
                  </template>
                </tbody>
                <tfoot class="table-secondary">
                  <tr>
                    <td colspan="3" class="text-end fw-bold">Total:</td>
                    <td class="fw-bold">
                      Q{{ calcularTotalDetalles().toFixed(2) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div v-else class="text-center text-muted py-3">
              No hay detalles para mostrar.
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="cerrarModalDetalles">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Pregunta Factura -->
    <div
      v-if="mostrarModalFactura"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">🧾 Tipo de Pago</h5>
            <button class="btn-close" @click="cerrarModalFactura"></button>
          </div>
          <div class="modal-body text-center">
            <!-- Paso 1: ¿Necesita factura? -->
            <div v-if="pasoModalFactura === 1">
              <p class="mb-4">¿Desea generar factura para este pago?</p>
              <div class="d-flex justify-content-center gap-3">
                <button class="btn btn-success" @click="avanzarANitOCf">
                  ✅ Sí, con factura
                </button>
                <button
                  class="btn btn-secondary"
                  @click="avanzarASinFactura"
                >
                  ❌ No, sin factura
                </button>
              </div>
            </div>
            <!-- Paso 1b: ¿Pago normal o a crédito? (sin factura) -->
            <div v-else-if="pasoModalFactura === 3">
              <p class="mb-4">¿Cómo desea registrar el pago?</p>
              <div class="d-flex justify-content-center gap-3 flex-wrap">
                <button class="btn btn-primary" @click="seleccionarSinFactura">
                  💳 Pago normal
                </button>
                <button class="btn btn-warning" @click="seleccionarPagoCredito">
                  📋 Pago a crédito
                </button>
              </div>
              <div class="mt-3">
                <button class="btn btn-link btn-sm text-muted" @click="pasoModalFactura = 1">
                  ← Volver
                </button>
              </div>
            </div>
            <!-- Paso 2: ¿NIT o CF? -->
            <div v-else-if="pasoModalFactura === 2">
              <p class="mb-4">
                ¿La factura va a nombre de un NIT o Consumidor Final?
              </p>
              <div class="d-flex justify-content-center gap-3">
                <button
                  class="btn btn-primary"
                  @click="seleccionarTipoFactura(true)"
                >
                  🪦 Con NIT
                </button>
                <button
                  class="btn btn-secondary"
                  @click="seleccionarTipoFactura(false)"
                >
                  👤 Consumidor Final (CF)
                </button>
              </div>
              <div class="mt-3">
                <button
                  class="btn btn-link btn-sm text-muted"
                  @click="pasoModalFactura = 1"
                >
                  ← Volver
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Pago -->
    <div
      v-if="mostrarModalPago"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">💳 Registrar Pago</h5>
            <button class="btn-close" @click="cerrarModalPago"></button>
          </div>
          <div class="modal-body">
            <!-- Saldo pendiente -->
            <div class="alert alert-info">
              <strong>Saldo Pendiente:</strong> Q{{
                parseFloat(
                  cuenta.SaldoPendienteCuenta ||
                    cuenta.TotalFinal ||
                    cuenta.Total,
                ).toFixed(2)
              }}
            </div>

            <!-- Pago Combinado: repartir el total entre varios métodos con una sola factura -->
            <div v-if="generarFactura" class="mb-3 form-check">
              <input
                id="checkPagoCombinadoRestaurante"
                v-model="pagoCombinado"
                @change="onPagoCombinadoChange"
                type="checkbox"
                class="form-check-input"
              />
              <label class="form-check-label fw-semibold" for="checkPagoCombinadoRestaurante">
                🧩 Pago Combinado: generar solo una factura repartida entre varios métodos de pago
              </label>
            </div>

            <!-- Datos FEL receptor -->
            <div v-if="requiereFactura" class="mb-3">
              <label class="form-label fw-semibold">NIT del Receptor</label>
              <div class="input-group">
                <input
                  v-model="nitReceptor"
                  @input="nitVerificado = false"
                  type="text"
                  class="form-control"
                  placeholder="Ej: 1234567"
                />
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  @click="verificarNitManual"
                  :disabled="cargandoVerificacionNit || !nitReceptor.trim()"
                >
                  <span
                    v-if="cargandoVerificacionNit"
                    class="spinner-border spinner-border-sm"
                  ></span>
                  <span v-else>Verificar</span>
                </button>
              </div>
              <small v-if="nitVerificado" class="text-success"
                ><i class="bi bi-check-circle-fill me-1"></i>NIT
                verificado</small
              >
              <small v-else-if="nitReceptor.trim()" class="text-warning"
                ><i class="bi bi-exclamation-triangle me-1"></i>Debes verificar
                el NIT antes de pagar</small
              >
            </div>
            <div v-if="requiereFactura" class="mb-3">
              <label class="form-label fw-semibold">Nombre del Receptor</label>
              <input
                v-model="nombreReceptor"
                type="text"
                class="form-control"
                placeholder="Nombre completo"
              />
            </div>
            <div
              v-if="!requiereFactura"
              class="alert alert-secondary py-2 mb-3"
            >
              <small>🧾 Factura: <strong>Consumidor Final (CF)</strong></small>
            </div>
            <div v-if="generarFactura" class="mb-3">
              <label class="form-label fw-semibold">Dirección (opcional)</label>
              <input
                v-model="direccionReceptor"
                type="text"
                class="form-control"
                placeholder="Ciudad"
              />
            </div>

            <!-- Checkbox: Es extranjero (solo si no hay factura FEL) -->
            <div v-if="!generarFactura" class="mb-3">
              <div class="form-check">
                <input
                  id="checkExtranjeroRest"
                  v-model="esExtranjero"
                  type="checkbox"
                  class="form-check-input"
                />
                <label class="form-check-label" for="checkExtranjeroRest">
                  🌍 Cliente extranjero (factura externa)
                </label>
              </div>
              <!-- Campo número factura manual -->
              <div v-if="esExtranjero" class="mt-2">
                <label class="form-label small fw-semibold"
                  >Número de Factura (Opcional)</label
                >
                <input
                  v-model="numeroFacturaManual"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Ej: A-001-00123456"
                  maxlength="50"
                />
                <small class="text-muted d-block mt-1"
                  >Factura generada fuera del sistema, solo para
                  referencia</small
                >
              </div>
            </div>

            <!-- Descripción detallada / Otros servicios -->
            <div v-if="generarFactura" class="mb-2 form-check">
              <input
                id="checkDetalladoRest"
                v-model="descripcionDetallada"
                @change="onDetalladoChange"
                type="checkbox"
                class="form-check-input"
                :disabled="otroServicio"
              />
              <label
                class="form-check-label"
                :class="{ 'text-muted': otroServicio }"
                for="checkDetalladoRest"
              >
                Detallar factura (descripción, cantidad y precio unitario)
              </label>
            </div>
            <!-- Líneas de la factura detallada: ej. "Desayunos" · 11 · Q50.00 -->
            <div v-if="generarFactura && descripcionDetallada" class="mb-3">
              <EditorLineasFactura
                :total-objetivo="totalFacturaDetallada"
                :descripcion-sugerida="cuenta?.NombreCuenta || 'Consumo Restaurante'"
                v-model="lineasDetalleRestaurante"
                @update:valido="lineasDetalleValidas = $event"
              />
            </div>
            <div v-if="generarFactura" class="mb-3 form-check">
              <input
                id="checkOtroServicio"
                v-model="otroServicio"
                @change="onOtroServicioChange"
                type="checkbox"
                class="form-check-input"
                :disabled="descripcionDetallada"
              />
              <label
                class="form-check-label"
                :class="{ 'text-muted': descripcionDetallada }"
                for="checkOtroServicio"
              >
                Facturar como Otros servicios (solo IVA)
                <small v-if="descripcionDetallada" class="text-muted ms-1"
                  >— No aplica con factura detallada</small
                >
              </label>
            </div>

            <!-- Método de pago (un solo método) -->
            <template v-if="!pagoCombinado">
              <div class="mb-3">
                <label class="form-label fw-semibold">Método de Pago</label>
                <select v-model="metodoPagoSeleccionado" class="form-select">
                  <option :value="null" disabled>Seleccione un método</option>
                  <option
                    v-for="metodo in metodosPago"
                    :key="metodo.IdMetodoPago"
                    :value="metodo.IdMetodoPago"
                  >
                    {{ metodo.Metodo }}
                  </option>
                </select>
              </div>

              <!-- Cuenta Bancaria (solo si método es 2) -->
              <div v-if="metodoPagoSeleccionado === 2" class="mb-3">
                <label class="form-label fw-semibold">Cuenta Bancaria</label>
                <select v-model="cuentaBancariaSeleccionada" class="form-select">
                  <option :value="null" disabled>Seleccione una cuenta</option>
                  <option
                    v-for="cuenta in cuentasBancarias"
                    :key="cuenta.IdCuentaBancaria"
                    :value="cuenta.IdCuentaBancaria"
                  >
                    {{ cuenta.Banco }} - {{ cuenta.NombreCuenta }} ({{
                      cuenta.NumeroCuenta
                    }})
                  </option>
                </select>
              </div>

              <!-- Monto -->
              <div class="mb-3">
                <label class="form-label fw-semibold">Monto a Pagar (Q)</label>
                <input
                  v-model.number="montoPago"
                  type="number"
                  step="0.01"
                  min="0.01"
                  class="form-control"
                  placeholder="Ingrese el monto"
                />
              </div>

              <!-- Cambio -->
              <div
                v-if="cambioRestaurante > 0"
                class="alert alert-warning py-2 mb-3"
              >
                <div class="d-flex justify-content-between">
                  <span>💵 Efectivo recibido:</span>
                  <strong>Q{{ montoPago.toFixed(2) }}</strong>
                </div>
                <div class="d-flex justify-content-between border-top pt-1 mt-1">
                  <span>➡️ <strong>CAMBIO:</strong></span>
                  <strong class="fs-5"
                    >Q{{ cambioRestaurante.toFixed(2) }}</strong
                  >
                </div>
              </div>

              <!-- Número de Referencia (métodos: 2=Depósito, 3=BAC, 4=NEONET) -->
              <div v-if="[2, 3, 4].includes(metodoPagoSeleccionado)" class="mb-3">
                <label class="form-label fw-semibold">Número de Referencia</label>
                <input
                  v-model="numeroReferencia"
                  type="text"
                  class="form-control"
                  placeholder="Referencia de transacción"
                />
              </div>
            </template>

            <!-- Pago Combinado: repartir el total entre varios métodos -->
            <div v-else class="mb-3">
              <label class="form-label fw-semibold">Métodos de Pago</label>
              <SelectorPagosCombinados
                :total="totalPagoCombinado"
                :metodos-pago="metodosPago"
                :cuentas-bancarias="cuentasBancarias"
                v-model="pagosCombinados"
                @update:valido="pagosCombinadosValidos = $event"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="cerrarModalPago">
              Cancelar
            </button>
            <button
              v-if="!pagoCombinado"
              class="btn btn-success"
              @click="procesarPago"
              :disabled="
                cargando ||
                !metodoPagoSeleccionado ||
                !montoPago ||
                (metodoPagoSeleccionado === 2 && !cuentaBancariaSeleccionada) ||
                (requiereReferencia(metodoPagoSeleccionado) && !String(numeroReferencia).trim()) ||
                (requiereFactura && !nitVerificado) ||
                detalleFacturaInvalido
              "
            >
              <span
                v-if="cargando"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              <span v-if="cargando && requiereFactura"
                >Generando factura...</span
              >
              <span v-else>Confirmar Pago</span>
            </button>
            <button
              v-else
              class="btn btn-success"
              @click="procesarPagoCombinado"
              :disabled="cargandoPagoCombinado || !pagosCombinadosValidos || (requiereFactura && !nitVerificado) || detalleFacturaInvalido"
            >
              <span v-if="cargandoPagoCombinado" class="spinner-border spinner-border-sm me-2"></span>
              <span v-if="cargandoPagoCombinado">Generando factura...</span>
              <span v-else>Confirmar Pago Combinado</span>
            </button>
          </div>
          <div
            v-if="(cargando && requiereFactura) || cargandoPagoCombinado"
            class="text-center py-2 border-top"
          >
            <small class="text-muted">
              <i class="bi bi-shield-check me-1 text-success"></i>
              Procesando factura electrónica con la SAT...
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Lista de Pagos -->
    <div
      v-if="mostrarModalPagos"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">💰 Pagos Realizados</h5>
            <button class="btn-close" @click="cerrarModalPagos"></button>
          </div>
          <div class="modal-body">
            <div v-if="pagos.length" class="table-responsive">
              <table class="table table-striped table-bordered align-middle">
                <thead class="bg-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Método</th>
                    <th>Monto</th>
                    <th>Referencia</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="pago in pagos" :key="pago.IdPagoCuentaRestaurante">
                    <td>{{ formatearFechaHora(pago.FechaHora) }}</td>
                    <td>
                      {{ pago.metodo_pago?.Metodo || "N/A" }}
                      <small
                        v-if="pago.cuenta_bancaria"
                        class="d-block text-muted"
                      >
                        {{ pago.cuenta_bancaria.NombreCuenta }} -
                        {{ pago.cuenta_bancaria.Banco }} -
                        {{ pago.cuenta_bancaria.NumeroCuenta }}
                      </small>
                    </td>
                    <td>Q{{ parseFloat(pago.Monto).toFixed(2) }}</td>
                    <td>{{ pago.NumeroReferencia || "-" }}</td>
                    <td>
                      <span
                        :class="
                          pago.Anulado === 1
                            ? 'badge bg-danger'
                            : 'badge bg-success'
                        "
                      >
                        {{ pago.Anulado === 1 ? "Anulado" : "Activo" }}
                      </span>
                    </td>
                    <td>
                      <div v-if="pago.factura_fel?.uuid" class="btn-group me-1">
                        <button
                          class="btn btn-outline-warning btn-sm"
                          @click="imprimirFacturaCarta(pago.factura_fel.uuid)"
                          title="Imprimir factura en hoja carta"
                        >📝 Factura</button>
                        <button
                          class="btn btn-outline-warning btn-sm"
                          @click="descargarFacturaCarta(pago.factura_fel.uuid)"
                          title="Descargar factura carta (PDF)"
                        ><i class="bx bxs-download"></i></button>
                      </div>
                      <div v-if="pago.factura_fel?.uuid" class="btn-group me-1">
                        <button
                          class="btn btn-outline-secondary btn-sm"
                          @click="imprimirTicketTermica(pago.factura_fel.uuid)"
                          title="Imprimir ticket térmico 80mm"
                        >🧾 Térmica</button>
                        <button
                          class="btn btn-outline-secondary btn-sm"
                          @click="descargarTicketTermica(pago.factura_fel.uuid)"
                          title="Descargar ticket térmico (PDF)"
                        ><i class="bx bxs-download"></i></button>
                      </div>
                      <div v-if="pago.factura_fel?.uuid" class="btn-group me-1">
                        <button
                          class="btn btn-outline-success btn-sm"
                          @click="imprimirPdfFel(pago.factura_fel.uuid)"
                          title="Reimprimir factura electrónica (TotalDoc)"
                        >📄 Cert.</button>
                        <button
                          class="btn btn-outline-success btn-sm"
                          @click="descargarPdfFel(pago.factura_fel.uuid)"
                          title="Descargar PDF del certificador"
                        ><i class="bx bxs-download"></i></button>
                      </div>
                      <button
                        class="btn btn-outline-primary btn-sm me-1"
                        @click="reimprimirReciboNormal(pago)"
                        title="Reimprimir recibo pequeño"
                      >
                        🖨️ Recibo
                      </button>
                      <button
                        v-if="pago.Anulado === 0 && !pago.factura_fel && pago.metodo_pago?.UsoMetodo === 2 && !pago.cuenta_bancaria"
                        class="btn btn-outline-success btn-sm me-1"
                        @click="abrirModalReemitir(pago)"
                        title="Generar factura electrónica para este pago"
                      >
                        🧾 Generar Factura
                      </button>
                      <button
                        v-if="pago.Anulado === 0"
                        class="btn btn-outline-danger btn-sm"
                        @click="confirmarAnularPago(pago)"
                        :disabled="cargando"
                      >
                        ❌ Cancelar pago
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-center text-muted py-3">
              No hay pagos registrados para esta cuenta.
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="cerrarModalPagos">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Confirmación Cancelar Pago -->
    <div
      v-if="mostrarConfirmacionAnular"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.6)"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title">⚠️ Cancelar Pago</h5>
            <button
              class="btn-close btn-close-white"
              @click="cancelarAnulacion"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="pagoAAnular" class="alert alert-warning mb-3">
              <strong>Pago a cancelar:</strong><br />
              Monto:
              <span class="fw-bold"
                >Q{{ parseFloat(pagoAAnular.Monto).toFixed(2) }}</span
              ><br />
              Método: {{ pagoAAnular.metodo_pago?.Metodo || "N/A" }}<br />
              Fecha: {{ formatearFechaHora(pagoAAnular.FechaHora) }}
            </div>
            <p class="mb-2 fw-semibold">Esta acción realizará lo siguiente:</p>
            <ul class="text-muted small mb-0">
              <li>
                Se cancelará el pago y se restaurará el saldo pendiente de la
                cuenta.
              </li>
              <li>El pago será eliminado de los registros contables.</li>
              <li v-if="pagoAAnular && pagoAAnular.factura_fel">
                La factura electrónica asociada será removida de los reportes.
              </li>
            </ul>
            <p class="text-danger small mt-2 mb-0">
              <strong>Esta acción no se puede deshacer.</strong>
            </p>
          </div>
          <div class="modal-footer justify-content-center">
            <button class="btn btn-secondary" @click="cancelarAnulacion">
              No, mantener pago
            </button>
            <button
              class="btn btn-danger"
              @click="anularPago"
              :disabled="cargando"
            >
              <span
                v-if="cargando"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Sí, cancelar pago
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Aplicar Código de Descuento -->
    <div
      v-if="mostrarModalDescuento"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Aplicar Código de Descuento</h5>
            <button class="btn-close" @click="cerrarModalDescuento"></button>
          </div>
          <div class="modal-body">
            <!-- Saldo pendiente -->
            <div class="alert alert-info">
              <strong>Saldo Pendiente:</strong> Q{{
                parseFloat(cuenta?.SaldoPendienteCuenta || 0).toFixed(2)
              }}
            </div>

            <!-- Código de descuento -->
            <div class="mb-3">
              <label class="form-label fw-semibold">Código de Descuento</label>
              <input
                v-model="codigoDescuento"
                type="text"
                class="form-control"
                placeholder="Ingrese el código de descuento"
                @keyup.enter="aplicarDescuento"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="cerrarModalDescuento">
              Cancelar
            </button>
            <button
              class="btn btn-success"
              @click="aplicarDescuento"
              :disabled="cargandoDescuento || !codigoDescuento"
            >
              <span
                v-if="cargandoDescuento"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Aplicar Descuento
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <div
      v-if="mostrarToast"
      class="toast-container position-fixed top-0 end-0 p-3"
      style="z-index: 1055"
    >
      <div class="toast show align-items-center text-white" :class="toastColor">
        <div class="d-flex">
          <div class="toast-body">
            <i :class="iconoToast" class="me-2"></i>
            {{ mensajeToast }}
          </div>
          <button
            type="button"
            class="btn-close btn-close-white me-2 m-auto"
            @click="cerrarToast"
          ></button>
        </div>
      </div>
    </div>

    <!-- Modal Historial de Unificaciones -->
    <div
      v-if="mostrarModalUnificaciones"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">🔗 Historial de Unificaciones</h5>
            <button class="btn-close" @click="mostrarModalUnificaciones = false"></button>
          </div>
          <div class="modal-body">
            <div v-if="cargandoUnificaciones" class="text-center py-3">
              <div class="spinner-border text-primary"></div>
            </div>
            <div v-else-if="unificaciones.length === 0" class="text-center text-muted py-3">
              Esta cuenta no ha absorbido otras cuentas.
            </div>
            <table v-else class="table table-sm table-bordered align-middle">
              <thead class="table-light">
                <tr>
                  <th>Cuenta Absorbida</th>
                  <th>Monto</th>
                  <th>Fecha</th>
                  <th>Estado</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="u in unificaciones" :key="u.IdUnificacion">
                  <td>{{ u.cuenta_origen?.NombreCuenta || '#' + u.IdCuentaOrigen }}</td>
                  <td>Q{{ parseFloat(u.MontoTransferido).toFixed(2) }}</td>
                  <td>{{ formatearFechaHora(u.FechaHora) }}</td>
                  <td>
                    <span :class="u.Activa ? 'badge bg-success' : 'badge bg-secondary'">
                      {{ u.Activa ? 'Activa' : 'Deshecha' }}
                    </span>
                  </td>
                  <td>
                    <button
                      v-if="u.Activa"
                      class="btn btn-sm btn-outline-danger"
                      @click="deshacerUnificacion(u)"
                      :disabled="cargandoDeshacerUnificacion === u.IdUnificacion"
                      :title="u.puedeDeshacer === false ? 'No se puede deshacer: ya existe un pago sobre esta cuenta' : 'Deshacer unificación'"
                    >
                      <span v-if="cargandoDeshacerUnificacion === u.IdUnificacion" class="spinner-border spinner-border-sm"></span>
                      <span v-else>↩ Deshacer</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="mostrarModalUnificaciones = false">Cerrar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Cambiar Habitación (cuando cuenta.Estado === 1) -->
    <div
      v-if="mostrarModalCambiarHabitacion"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">🔄 Cambiar Habitación</h5>
            <button class="btn-close" @click="cerrarModalCambiarHabitacion"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small mb-3">
              Busque la nueva reserva/habitación a la que se cargará esta cuenta.
              Los saldos de ambas habitaciones se actualizarán automáticamente.
            </p>
            <label class="form-label">Buscar Reserva</label>
            <input
              v-model="cambiarBusquedaReserva"
              class="form-control mb-2"
              placeholder="Código de reserva, nombre o habitación"
              @input="debouncedBuscarReservasCambio"
            />
            <ul
              class="list-group mt-1"
              v-if="Array.isArray(cambiarReservasFiltradas) && cambiarReservasFiltradas.length"
            >
              <li
                v-for="res in cambiarReservasFiltradas"
                :key="res.IdReserva"
                class="list-group-item"
              >
                <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
                  <span class="fw-semibold">R00{{ res.IdReserva }}</span>
                  <span>–</span>
                  <span>{{ res.huesped?.Nombre }} {{ res.huesped?.Apellido }}</span>
                </div>
                <ul v-if="Array.isArray(res.habitaciones_info) && res.habitaciones_info.length" class="list-group ms-3">
                  <li
                    v-for="hab in res.habitaciones_info"
                    :key="hab.IdReservaHabitacion"
                    class="list-group-item list-group-item-action"
                    :class="{ active: cambiarHabitacionSeleccionada?.IdReservaHabitacion === hab.IdReservaHabitacion }"
                    @click="seleccionarHabitacionCambio(res, hab)"
                  >
                    🛏️ Habitación {{ hab.Numero }}
                  </li>
                </ul>
              </li>
            </ul>
            <div v-if="cambiarHabitacionSeleccionada" class="alert alert-success mt-3">
              ✅ Se cambiará a la habitación <strong>{{ cambiarHabitacionSeleccionada.Numero }}</strong>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="cerrarModalCambiarHabitacion" :disabled="cargandoCambioHabitacion">Cancelar</button>
            <button
              class="btn btn-primary"
              @click="confirmarCambioHabitacion"
              :disabled="!cambiarHabitacionSeleccionada || cargandoCambioHabitacion"
            >
              <span v-if="cargandoCambioHabitacion" class="spinner-border spinner-border-sm me-2"></span>
              Confirmar Cambio
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Pago a Crédito -->
    <div
      v-if="mostrarModalCredito"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-warning text-dark">
            <h5 class="modal-title">📋 Registrar Pago a Crédito</h5>
            <button class="btn-close" @click="cerrarModalCredito"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-info">
              <strong>Monto a crédito:</strong> Q{{
                parseFloat(cuenta?.SaldoPendienteCuenta || cuenta?.Total || 0).toFixed(2)
              }}
            </div>
            <p class="text-muted small mb-3">
              El saldo de la cuenta quedará en cero. El crédito deberá ser cobrado antes de la fecha de vencimiento.
            </p>
            <div class="mb-3">
              <label class="form-label fw-semibold">Nombre del cliente</label>
              <input
                v-model="creditoNombre"
                type="text"
                class="form-control"
                placeholder="Nombre completo"
              />
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">NIT (opcional)</label>
              <input
                v-model="creditoNit"
                type="text"
                class="form-control"
                placeholder="CF si no aplica"
              />
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">Descripción del gasto</label>
              <input
                v-model="creditoDescripcion"
                type="text"
                class="form-control"
                placeholder="Ej: Consumo de restaurante - Mesa 3"
              />
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">Fecha de vencimiento <span class="text-danger">*</span></label>
              <input
                v-model="creditoFechaVencimiento"
                type="date"
                class="form-control"
                :min="fechaMinimaCredito"
              />
              <small class="text-muted">Debe ser una fecha futura.</small>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="cerrarModalCredito" :disabled="cargandoCredito">
              Cancelar
            </button>
            <button
              class="btn btn-warning"
              @click="procesarPagoCredito"
              :disabled="cargandoCredito || !creditoFechaVencimiento"
            >
              <span v-if="cargandoCredito" class="spinner-border spinner-border-sm me-2"></span>
              Registrar Crédito
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Re-emitir FEL (generar factura para pago sin factura) -->
    <div
      v-if="mostrarModalReemitir"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-warning text-dark">
            <h5 class="modal-title">🧾 Generar Factura Electrónica</h5>
            <button class="btn-close" @click="cerrarModalReemitir"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small mb-3">
              Se generará una factura FEL para el pago de
              <strong>Q{{ parseFloat(reemitirPago?.Monto || 0).toFixed(2) }}</strong>.
              Deje en blanco para emitir a Consumidor Final (CF).
            </p>
            <div class="mb-3">
              <label class="form-label fw-semibold">NIT del receptor</label>
              <input
                v-model="reemitirNit"
                type="text"
                class="form-control"
                placeholder="CF (Consumidor Final)"
              />
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">Nombre del receptor</label>
              <input
                v-model="reemitirNombre"
                type="text"
                class="form-control"
                placeholder="Consumidor Final"
              />
            </div>
          </div>
          <div class="modal-footer justify-content-center">
            <button
              class="btn btn-secondary"
              @click="cerrarModalReemitir"
              :disabled="cargandoReemitir"
            >
              Cancelar
            </button>
            <button
              class="btn btn-warning"
              @click="reemitirFactura"
              :disabled="cargandoReemitir"
            >
              <span
                v-if="cargandoReemitir"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              🧾 Generar Factura
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_URL } from "@/config/config";
import debounce from "lodash/debounce";
import { imprimirReciboRestaurante } from "@/utils/impresion";
import SelectorPagosCombinados from "@/components/comunes/SelectorPagosCombinados.vue";
import EditorLineasFactura from "@/components/comunes/EditorLineasFactura.vue";

const MENSAJE_DETALLE_INVALIDO =
  "Revisa el detalle de la factura: las líneas deben sumar el total a facturar";

export default {
  name: "PagarCuentaPedido",
  components: { SelectorPagosCombinados, EditorLineasFactura },
  data() {
    return {
      busquedaReserva: "",
      reservasFiltradas: [],
      reservaSeleccionada: null,
      habitacionSeleccionada: null,
      debouncedBuscarReservas: null,
      tipoPago: null,
      cuenta: null,
      mesa: null,
      detalles: [],
      cargando: false,
      mostrarToast: false,
      mensajeToast: "",
      tipoToast: "success",
      mostrarModalDetalles: false,
      propinaActiva: true,
      propinaPersonalizada: null, // null = usa el 10% automático
      // Nuevas propiedades para el flujo de pago
      mostrarModalFactura: false,
      pasoModalFactura: 1,
      mostrarModalPago: false,
      requiereFactura: false,
      generarFactura: false,
      descripcionDetallada: false,
      lineasDetalleRestaurante: [],
      lineasDetalleValidas: false,
      otroServicio: false,
      metodosPago: [],
      metodoPagoSeleccionado: null,
      montoPago: null,
      numeroReferencia: "",
      // Nuevas propiedades para cuentas bancarias
      cuentasBancarias: [],
      cuentaBancariaSeleccionada: null,
      // Propiedades para lista de pagos
      pagos: [],
      mostrarModalPagos: false,
      pagoAAnular: null,
      mostrarConfirmacionAnular: false,
      // Propiedades para código de descuento
      mostrarModalDescuento: false,
      codigoDescuento: "",
      cargandoDescuento: false,
      // Quitar descuento (F4 Parte D)
      modalQuitarDescuentoVisible: false,
      cargandoQuitarDescuento: false,
      // Datos FEL receptor
      nitReceptor: "",
      nombreReceptor: "",
      direccionReceptor: "",
      nitVerificado: false,
      cargandoVerificacionNit: false,
      esperandoImpresion: false,
      // Pago de extranjeros sin factura FEL
      esExtranjero: false,
      numeroFacturaManual: "",
      // Re-emitir FEL para pagos sin factura
      mostrarModalReemitir: false,
      reemitirPago: null,
      reemitirNit: "",
      reemitirNombre: "",
      cargandoReemitir: false,
      // Historial de unificaciones
      mostrarModalUnificaciones: false,
      unificaciones: [],
      cargandoUnificaciones: false,
      cargandoDeshacerUnificacion: null,
      // Cambiar habitación (Estado=1)
      mostrarModalCambiarHabitacion: false,
      cambiarBusquedaReserva: "",
      cambiarReservasFiltradas: [],
      cambiarHabitacionSeleccionada: null,
      cargandoCambioHabitacion: false,
      debouncedBuscarReservasCambio: null,
      // Pago a crédito
      mostrarModalCredito: false,
      creditoNombre: "",
      creditoNit: "",
      creditoDescripcion: "",
      creditoFechaVencimiento: "",
      cargandoCredito: false,
      // Pago Combinado (multi-método) — checkbox dentro del mismo modal de
      // "Registrar Pago" (mostrarModalPago), no un modal aparte. Reutiliza
      // requiereFactura/generarFactura/nitReceptor/etc. del flujo normal.
      pagoCombinado: false,
      pagosCombinados: [],
      pagosCombinadosValidos: false,
      cargandoPagoCombinado: false,
      grupoPagoCombinado: "",
    };
  },
  computed: {
    propinaAutoCalculada() {
      if (!this.cuenta) return "0.00";
      const subtotal = parseFloat(this.cuenta.Subtotal || 0);
      return Math.floor(subtotal * 0.1).toFixed(2);
    },
    toastColor() {
      return this.tipoToast === "success" ? "bg-success" : "bg-danger";
    },
    iconoToast() {
      return this.tipoToast === "success"
        ? "bi bi-check-circle"
        : "bi bi-exclamation-circle";
    },
    estadoTexto() {
      switch (this.cuenta?.Estado) {
        case 0:
          return "Pendiente";
        case 1:
          return "Cargada a Habitación";
        case 2:
          return "Pagada";
        default:
          return "Desconocido";
      }
    },
    estadoBadgeClass() {
      switch (this.cuenta?.Estado) {
        case 0:
          return "badge bg-danger";
        case 1:
          return "badge bg-info";
        case 2:
          return "badge bg-success";
        default:
          return "badge bg-secondary";
      }
    },
    fechaMinimaCredito() {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      return d.toISOString().split("T")[0];
    },
    cambioRestaurante() {
      const metodo = this.metodosPago.find(
        (m) => m.IdMetodoPago === this.metodoPagoSeleccionado,
      );
      const esEfectivo =
        metodo?.Metodo?.toLowerCase().includes("efectivo") ?? false;
      const saldo = parseFloat(
        this.cuenta?.SaldoPendienteCuenta ||
          this.cuenta?.TotalFinal ||
          this.cuenta?.Total ||
          0,
      );
      return esEfectivo && this.montoPago > saldo
        ? Math.round((this.montoPago - saldo) * 100) / 100
        : 0;
    },
    totalPagoCombinado() {
      return parseFloat(
        this.cuenta?.SaldoPendienteCuenta ||
          this.cuenta?.TotalFinal ||
          this.cuenta?.Total ||
          0,
      );
    },
    // Monto que se factura: el efectivo recibido de más es vuelto (el backend lo recorta igual)
    totalFacturaDetallada() {
      const monto = this.pagoCombinado
        ? this.totalPagoCombinado
        : Math.min(Number(this.montoPago) || 0, this.totalPagoCombinado);
      return Math.round(monto * 100) / 100;
    },
    detalleFacturaInvalido() {
      return (
        this.generarFactura &&
        this.descripcionDetallada &&
        !this.lineasDetalleValidas
      );
    },
  },
  watch: {
    tipoPago(nuevoTipo) {
      if (nuevoTipo === "pagar") {
        this.pasoModalFactura = 1;
        this.mostrarModalFactura = true;
      }
    },
    async metodoPagoSeleccionado(nuevoMetodo) {
      if (nuevoMetodo === 2) {
        await this.cargarCuentasBancarias();
      } else {
        this.cuentasBancarias = [];
        this.cuentaBancariaSeleccionada = null;
        this.numeroReferencia = "";
      }
    },
  },

  methods: {
    descargarFacturaCarta(uuid) {
      import("axios").then(({ default: axios }) => {
        axios.get(`facturas-fel/${uuid}/carta`, { responseType: "blob" }).then(res => {
          const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
          const a = document.createElement("a");
          a.href = url; a.download = `factura-${uuid}.pdf`; a.click();
          URL.revokeObjectURL(url);
        }).catch(() => {});
      });
    },
    descargarTicketTermica(uuid) {
      import("axios").then(({ default: axios }) => {
        axios.get(`facturas-fel/${uuid}/ticket`, { responseType: "blob" }).then(res => {
          const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
          const a = document.createElement("a");
          a.href = url; a.download = `ticket-${uuid}.pdf`; a.click();
          URL.revokeObjectURL(url);
        }).catch(() => {});
      });
    },
    descargarPdfFel(uuid) {
      import("axios").then(({ default: axios }) => {
        axios.get(`facturas-fel/${uuid}/pdf`, { responseType: "blob" }).then(res => {
          const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
          const a = document.createElement("a");
          a.href = url; a.download = `certificador-${uuid}.pdf`; a.click();
          URL.revokeObjectURL(url);
        }).catch(() => {});
      });
    },
    imprimirPdfFel(uuid) {
      // Fetch del PDF a través del proxy del backend (sirve inline con auth)
      import("axios").then(({ default: axios }) => {
        axios
          .get(`facturas-fel/${uuid}/pdf`, { responseType: "blob" })
          .then((res) => {
            const blobUrl = URL.createObjectURL(
              new Blob([res.data], { type: "application/pdf" }),
            );
            const existente = document.getElementById("fel-pdf-frame");
            if (existente) existente.remove();
            const iframe = document.createElement("iframe");
            iframe.id = "fel-pdf-frame";
            iframe.style.cssText =
              "position:fixed;width:1px;height:1px;opacity:0;border:none;";
            iframe.src = blobUrl;
            document.body.appendChild(iframe);
            iframe.onload = () => {
              try {
                iframe.contentWindow.print();
              } catch (e) {
                /* bloqueado por el navegador */
              }
            };
          })
          .catch(() => {
            /* fallo silencioso */
          });
      });
    },
    imprimirTicketTermica(uuid, cambio = 0) {
      // Genera ticket térmico 80mm desde el backend y abre el diálogo de impresión
      import("axios").then(({ default: axios }) => {
        const params = cambio > 0 ? { cambio } : {};
        axios
          .get(`facturas-fel/${uuid}/ticket`, { responseType: "blob", params })
          .then((res) => {
            const blobUrl = URL.createObjectURL(
              new Blob([res.data], { type: "application/pdf" }),
            );
            const existente = document.getElementById("fel-ticket-frame");
            if (existente) existente.remove();
            const iframe = document.createElement("iframe");
            iframe.id = "fel-ticket-frame";
            iframe.style.cssText =
              "position:fixed;width:1px;height:1px;opacity:0;border:none;";
            iframe.src = blobUrl;
            document.body.appendChild(iframe);
            iframe.onload = () => {
              try {
                this.esperandoImpresion = false;
                iframe.contentWindow.print();
              } catch (e) {
                this.esperandoImpresion = false;
                window.open(blobUrl, "_blank");
              }
            };
          })
          .catch(() => {
            this.esperandoImpresion = false;
          });
      });
    },
    imprimirFacturaCarta(uuid) {
      // Genera factura en hoja carta (estilo formal café) y abre el diálogo de impresión
      import("axios").then(({ default: axios }) => {
        axios
          .get(`facturas-fel/${uuid}/carta`, { responseType: "blob" })
          .then((res) => {
            const blobUrl = URL.createObjectURL(
              new Blob([res.data], { type: "application/pdf" }),
            );
            const existente = document.getElementById("fel-carta-frame");
            if (existente) existente.remove();
            const iframe = document.createElement("iframe");
            iframe.id = "fel-carta-frame";
            iframe.style.cssText =
              "position:fixed;width:1px;height:1px;opacity:0;border:none;";
            iframe.src = blobUrl;
            document.body.appendChild(iframe);
            iframe.onload = () => {
              try {
                iframe.contentWindow.print();
              } catch (e) {
                window.open(blobUrl, "_blank");
              }
            };
          });
      });
    },
    calcularTotalDetalles() {
      let total = 0;
      for (const item of this.detalles) {
        // Sumar precio base del platillo
        total += parseFloat(item.precio_unitario) * item.cantidad;
        // Sumar extras/opciones
        if (item.opciones && item.opciones.length) {
          for (const opcion of item.opciones) {
            total += parseFloat(opcion.precio_extra) * opcion.cantidad;
          }
        }
      }
      return total;
    },
    formatearFecha(fecha) {
      if (!fecha) return "";
      const [year, month, day] = fecha.split("-");
      return `${day}/${month}/${year}`;
    },
    formatearFechaHora(fechaHora) {
      if (!fechaHora) return "";
      const fecha = new Date(fechaHora);
      const dia = fecha.getDate().toString().padStart(2, "0");
      const mes = (fecha.getMonth() + 1).toString().padStart(2, "0");
      const anio = fecha.getFullYear();
      const hora = fecha.getHours().toString().padStart(2, "0");
      const minutos = fecha.getMinutes().toString().padStart(2, "0");
      return `${dia}/${mes}/${anio} ${hora}:${minutos}`;
    },
    limpiarTelefono(telefono) {
      if (!telefono) return "Sin teléfono";
      return telefono.replace(/^502/, "");
    },
    seleccionarHabitacion(reserva, habitacion) {
      this.reservaSeleccionada = reserva;
      this.habitacionSeleccionada = habitacion;
      this.busquedaReserva = `R00${reserva.IdReserva} - Habitación ${habitacion.Numero}`;
      this.reservasFiltradas = [];
    },
    async buscarReservas() {
      if (this.busquedaReserva.length < 2) {
        this.reservasFiltradas = [];
        return;
      }

      try {
        const res = await axios.get(`${API_URL}/reservas/buscar-avanzado`, {
          params: { q: this.busquedaReserva, solo_checkin_activo: 1 },
        });
        this.reservasFiltradas = Array.isArray(res.data.data)
          ? res.data.data
          : [];
      } catch (err) {
        console.error(err);
        this.reservasFiltradas = [];
      }
    },
    async cargarCuenta() {
      const idCuenta = this.$route.params.idCuenta;
      try {
        const { data } = await axios.get(
          `${API_URL}/pagar-cuenta-pedido/${idCuenta}`,
        );
        this.cuenta = data.cuenta;
        this.detalles = data.detalles;
        this.mesa = data.mesa;
        // Actualiza el estado del switch según si la propina es mayor a 0
        this.propinaActiva = parseFloat(this.cuenta.Propina) > 0;
        // No es necesario guardar EstadoPropina en data, se usa directamente desde cuenta
      } catch (err) {
        console.error("Error al cargar la cuenta:", err);
        this.mostrarToastMsg("Error al cargar la cuenta", "danger");
      }
    },
    generarUUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    },
    // ── Pago Combinado (multi-método) ───────────────────────────────────────
    // Vive DENTRO del mismo modal de "Registrar Pago": el checkbox
    // "pagoCombinado" alterna la vista de pago entre el <select> de un solo
    // método y el repetidor SelectorPagosCombinados. Reutiliza
    // requiereFactura/nitReceptor/nombreReceptor/etc. del flujo normal.
    onPagoCombinadoChange() {
      if (this.pagoCombinado) {
        this.pagosCombinados = [];
        this.pagosCombinadosValidos = false;
        this.grupoPagoCombinado = this.generarUUID();
      }
    },
    async procesarPagoCombinado() {
      if (!this.pagosCombinadosValidos) {
        return this.mostrarToastMsg(
          "Revisa los métodos de pago capturados",
          "danger",
        );
      }
      if (this.requiereFactura && !this.nitVerificado) {
        return this.mostrarToastMsg(
          "Verifica el NIT antes de pagar",
          "danger",
        );
      }
      if (this.detalleFacturaInvalido) {
        return this.mostrarToastMsg(MENSAJE_DETALLE_INVALIDO, "danger");
      }

      try {
        this.cargandoPagoCombinado = true;
        const idCuenta = this.$route.params.idCuenta;

        const payload = {
          pagos: this.pagosCombinados.map((p) => ({
            IdMetodoPago: p.IdMetodoPago,
            Monto: p.Monto,
            IdCuentaBancaria: p.IdMetodoPago === 2 ? p.IdCuentaBancaria : null,
            NumeroReferencia: this.requiereReferencia(p.IdMetodoPago)
              ? p.NumeroReferencia
              : null,
          })),
          GrupoPago: this.grupoPagoCombinado,
          receptor_nit: this.requiereFactura
            ? this.nitReceptor.trim()
            : "CF",
          receptor_nombre: this.requiereFactura
            ? this.nombreReceptor.trim() || "Consumidor Final"
            : "Consumidor Final",
          receptor_direccion: this.direccionReceptor.trim() || null,
          descripcion_detallada: this.descripcionDetallada,
          lineas_detalle_restaurante: this.descripcionDetallada
            ? this.lineasDetalleRestaurante
            : null,
          otro_servicio: this.otroServicio,
        };

        const { data } = await axios.post(
          `${API_URL}/cuentas-restaurante/${idCuenta}/pagar-combinado`,
          payload,
        );

        this.mostrarToastMsg(
          data.message || "Pago combinado registrado correctamente",
          "success",
        );

        if (data.factura?.uuid) {
          this.esperandoImpresion = true;
          setTimeout(() => this.imprimirFacturaCarta(data.factura.uuid), 900);
        }

        await this.cargarCuenta();
        await this.cargarPagos();

        this.cerrarModalPago();
      } catch (err) {
        console.error(err);
        this.mostrarToastMsg(
          err.response?.data?.message || "Error al procesar el pago combinado",
          "danger",
        );
      } finally {
        this.cargandoPagoCombinado = false;
      }
    },
    iniciarPago() {
      if (this.tipoPago === "cargar_habitacion") {
        this.confirmarPago();
      } else {
        // Mostrar modal desde paso 1 (¿factura?)
        this.pasoModalFactura = 1;
        this.mostrarModalFactura = true;
      }
    },
    avanzarANitOCf() {
      this.pasoModalFactura = 2;
    },
    async seleccionarSinFactura() {
      this.requiereFactura = false;
      this.generarFactura = false;
      this.mostrarModalFactura = false;
      this.pasoModalFactura = 1;
      this.nitReceptor = "";
      this.nombreReceptor = "";
      this.direccionReceptor = "";
      try {
        this.cargando = true;
        const { data } = await axios.get(
          `${API_URL}/metodos-pago/no-facturados`,
        );
        this.metodosPago = Array.isArray(data) ? data : [];
        this.montoPago = parseFloat(
          this.cuenta.SaldoPendienteCuenta ||
            this.cuenta.TotalFinal ||
            this.cuenta.Total,
        );
        this.mostrarModalPago = true;
      } catch (err) {
        console.error(err);
        this.mostrarToastMsg("Error al cargar métodos de pago", "danger");
      } finally {
        this.cargando = false;
      }
    },
    async verificarNitManual() {
      if (!this.nitReceptor.trim()) return;
      this.cargandoVerificacionNit = true;
      this.nitVerificado = false;
      try {
        const { data: nitInfo } = await axios.get(
          `${API_URL}/facturas-fel/verificar-nit/${encodeURIComponent(
            this.nitReceptor.trim(),
          )}`,
        );
        if (nitInfo.valido) {
          this.nitVerificado = true;
          if (nitInfo.nombre) this.nombreReceptor = nitInfo.nombre;
          this.mostrarToastMsg("NIT verificado correctamente", "success");
        } else {
          this.mostrarToastMsg("NIT no válido o no encontrado", "danger");
        }
      } catch {
        this.mostrarToastMsg("Error al verificar el NIT", "danger");
      } finally {
        this.cargandoVerificacionNit = false;
      }
    },
    async seleccionarTipoFactura(conFactura) {
      this.requiereFactura = conFactura;
      this.generarFactura = true; // el usuario eligió "Sí" en paso 1
      this.mostrarModalFactura = false;
      this.pasoModalFactura = 1;

      // Auto-fill NIT del cliente si tiene reserva vinculada con NIT
      if (conFactura) {
        const nit =
          this.cuenta?.huesped?.NIT ||
          this.cuenta?.reservaHabitacion?.reserva?.huesped?.NIT ||
          "";
        if (nit) {
          this.nitReceptor = nit;
          try {
            const { data: nitInfo } = await axios.get(
              `${API_URL}/facturas-fel/verificar-nit/${encodeURIComponent(
                nit,
              )}`,
            );
            const h =
              this.cuenta?.huesped ||
              this.cuenta?.reservaHabitacion?.reserva?.huesped;
            this.nombreReceptor =
              nitInfo.valido && nitInfo.nombre
                ? nitInfo.nombre
                : [h?.Nombre, h?.Apellido].filter(Boolean).join(" ");
            this.nitVerificado = !!nitInfo.valido;
          } catch {
            const h =
              this.cuenta?.huesped ||
              this.cuenta?.reservaHabitacion?.reserva?.huesped;
            this.nombreReceptor = [h?.Nombre, h?.Apellido]
              .filter(Boolean)
              .join(" ");
          }
        } else {
          this.nitReceptor = "";
          this.nombreReceptor = "";
        }
      } else {
        this.nitReceptor = "";
        this.nombreReceptor = "";
      }

      try {
        this.cargando = true;
        // Cargar métodos de pago facturados
        const { data } = await axios.get(`${API_URL}/metodos-pago/facturados`);
        this.metodosPago = Array.isArray(data) ? data : [];

        // Establecer monto por defecto al saldo pendiente
        this.montoPago = parseFloat(
          this.cuenta.SaldoPendienteCuenta ||
            this.cuenta.TotalFinal ||
            this.cuenta.Total,
        );

        this.mostrarModalPago = true;
      } catch (err) {
        console.error("Error al cargar métodos de pago:", err);
        this.mostrarToastMsg("Error al cargar métodos de pago", "danger");
      } finally {
        this.cargando = false;
      }
    },
    cerrarModalFactura() {
      this.mostrarModalFactura = false;
      this.pasoModalFactura = 1;
    },
    cerrarModalPago() {
      this.mostrarModalPago = false;
      this.resetearFormularioPago();
    },
    onDetalladoChange() {
      if (this.descripcionDetallada) {
        this.otroServicio = false;
      } else {
        this.lineasDetalleRestaurante = [];
      }
    },
    onOtroServicioChange() {
      if (this.otroServicio) {
        this.descripcionDetallada = false;
        this.lineasDetalleRestaurante = [];
      }
    },
    async cargarCuentasBancarias() {
      try {
        const { data } = await axios.get(`${API_URL}/cuentas-bancarias`);
        this.cuentasBancarias = Array.isArray(data) ? data : [];
      } catch (err) {
        console.error("Error al cargar cuentas bancarias:", err);
        this.mostrarToastMsg("Error al cargar cuentas bancarias", "danger");
      }
    },
    resetearFormularioPago() {
      this.metodoPagoSeleccionado = null;
      this.montoPago = null;
      this.numeroReferencia = "";
      this.metodosPago = [];
      this.cuentasBancarias = [];
      this.cuentaBancariaSeleccionada = null;
      this.nitReceptor = "";
      this.nombreReceptor = "";
      this.direccionReceptor = "";
      this.nitVerificado = false;
      this.descripcionDetallada = false;
      this.otroServicio = false;
      this.lineasDetalleRestaurante = [];
      this.esExtranjero = false;
      this.numeroFacturaManual = "";
      this.pagoCombinado = false;
      this.pagosCombinados = [];
      this.pagosCombinadosValidos = false;
      this.grupoPagoCombinado = "";
    },
    requiereReferencia(idMetodo) {
      return [2, 3, 4].includes(Number(idMetodo));
    },
    async procesarPago() {
      if (!this.metodoPagoSeleccionado || !this.montoPago) {
        return this.mostrarToastMsg(
          "Complete todos los campos requeridos",
          "danger",
        );
      }

      if (
        this.metodoPagoSeleccionado === 2 &&
        !this.cuentaBancariaSeleccionada
      ) {
        return this.mostrarToastMsg("Seleccione una cuenta bancaria", "danger");
      }

      if (
        this.requiereReferencia(this.metodoPagoSeleccionado) &&
        !String(this.numeroReferencia).trim()
      ) {
        return this.mostrarToastMsg(
          "Ingrese el número de referencia de la transacción",
          "danger",
        );
      }

      if (this.requiereFactura && !this.nitReceptor.trim()) {
        return this.mostrarToastMsg(
          "Ingrese el NIT del receptor para la factura",
          "danger",
        );
      }
      if (this.detalleFacturaInvalido) {
        return this.mostrarToastMsg(MENSAJE_DETALLE_INVALIDO, "danger");
      }
      if (this.requiereFactura) {
        try {
          const { data: nitInfo } = await axios.get(
            `${API_URL}/facturas-fel/verificar-nit/${encodeURIComponent(
              this.nitReceptor.trim(),
            )}`,
          );
          if (!nitInfo.valido) {
            return this.mostrarToastMsg(
              "El NIT ingresado no existe en el RTU de la SAT. Verifique el NIT o use CF.",
              "danger",
            );
          }
        } catch (_e) {
          // Si el servicio de verificación no responde, validar solo el formato
          if (!/^\d{1,8}(-[\dK])?$/i.test(this.nitReceptor.trim())) {
            return this.mostrarToastMsg(
              "El NIT ingresado no tiene un formato válido. Ejemplo: 1234567-8 ó 1234567-K",
              "danger",
            );
          }
        }
      }

      try {
        this.cargando = true;
        const idCuenta = this.$route.params.idCuenta;

        const payload = {
          Monto: this.montoPago,
          IdMetodoPago: this.metodoPagoSeleccionado,
          NumeroReferencia: [2, 3, 4].includes(this.metodoPagoSeleccionado)
            ? this.numeroReferencia
            : null,
          IdCuentaBancaria:
            this.metodoPagoSeleccionado === 2
              ? this.cuentaBancariaSeleccionada
              : null,
          NumeroFacturaManual:
            !this.generarFactura && this.esExtranjero
              ? this.numeroFacturaManual.trim() || null
              : null,
          receptor_nit: this.requiereFactura ? this.nitReceptor.trim() : "CF",
          receptor_nombre: this.requiereFactura
            ? this.nombreReceptor.trim() || "Consumidor Final"
            : "Consumidor Final",
          receptor_direccion: this.generarFactura
            ? this.direccionReceptor.trim() || null
            : null,
          generar_factura: this.generarFactura,
          descripcion_detallada: this.descripcionDetallada ? 1 : 0,
          lineas_detalle_restaurante:
            this.generarFactura && this.descripcionDetallada
              ? this.lineasDetalleRestaurante
              : null,
          otro_servicio: this.otroServicio ? 1 : 0,
        };

        const { data } = await axios.post(
          `${API_URL}/cuentas-restaurante/${idCuenta}/pagar`,
          payload,
        );

        this.mostrarToastMsg(
          data.message || "Pago registrado correctamente",
          "success",
        );

        // Abrir e imprimir PDF de factura electrónica si se generó
        if (data.factura?.uuid) {
          this.esperandoImpresion = true;
          setTimeout(() => this.imprimirFacturaCarta(data.factura.uuid), 900);
        } else if (data.factura_error) {
          setTimeout(
            () =>
              this.mostrarToastMsg(
                "Aviso FEL: " + data.factura_error,
                "warning",
              ),
            3500,
          );
        }

        // Recargar cuenta para actualizar saldo
        await this.cargarCuenta();

        // Recargar lista de pagos para que los botones de factura aparezcan
        // correctamente al abrir "Ver Pagos Realizados"
        await this.cargarPagos();

        // Obtener información del método de pago
        const metodoPago = this.metodosPago.find(
          (m) => m.IdMetodoPago === this.metodoPagoSeleccionado,
        );
        const cuentaBancaria = this.cuentaBancariaSeleccionada
          ? this.cuentasBancarias.find(
              (c) => c.IdCuentaBancaria === this.cuentaBancariaSeleccionada,
            )
          : null;

        // Preparar información del pago para impresión
        const pagoParaImprimir = {
          Monto: this.montoPago,
          metodo_pago: metodoPago?.Metodo || "N/A",
          NumeroReferencia: this.numeroReferencia,
          cuenta_bancaria: cuentaBancaria
            ? `${cuentaBancaria.Banco} - ${cuentaBancaria.NombreCuenta} - ${cuentaBancaria.NumeroCuenta}`
            : null,
        };

        // Imprimir recibo solo si NO se generó factura electrónica
        if (!data.factura?.uuid) {
          setTimeout(() => {
            imprimirReciboRestaurante(
              pagoParaImprimir,
              this.cuenta,
              this.detalles,
            );
          }, 500);
        }

        this.cerrarModalPago();
      } catch (err) {
        console.error(err);
        this.mostrarToastMsg(
          err.response?.data?.message || "Error al procesar el pago",
          "danger",
        );
      } finally {
        this.cargando = false;
      }
    },
    async confirmarPago() {
      // 🔒 Validación previa
      if (
        this.tipoPago === "cargar_habitacion" &&
        !this.habitacionSeleccionada
      ) {
        return this.mostrarToastMsg(
          "Debe seleccionar una habitación para cargar la cuenta",
          "danger",
        );
      }

      try {
        this.cargando = true;
        const idCuenta = this.$route.params.idCuenta;

        // 🟡 CASO: CARGAR A HABITACIÓN
        if (this.tipoPago === "cargar_habitacion") {
          const { data } = await axios.put(
            `${API_URL}/pagar-cuenta-pedido/${idCuenta}/cargar-habitacion`,
            {
              IdReservaHabitacion:
                this.habitacionSeleccionada.IdReservaHabitacion,
            },
          );

          this.mostrarToastMsg(
            data.message || "Cuenta cargada correctamente a la habitación",
            "success",
          );

          // Recargar cuenta para actualizar estado
          await this.cargarCuenta();
        }
      } catch (err) {
        console.error(err);
        this.mostrarToastMsg(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "Error al procesar la operación",
          "danger",
        );
      } finally {
        this.cargando = false;
      }
    },
    async togglePropina() {
      const idCuenta = this.$route.params.idCuenta;
      try {
        this.cargando = true;
        const payload = { propina: this.propinaActiva ? 1 : 0 };
        // Si se desactiva, limpiar el monto personalizado
        if (!this.propinaActiva) {
          this.propinaPersonalizada = null;
        } else if (
          this.propinaPersonalizada !== null &&
          this.propinaPersonalizada !== ""
        ) {
          payload.monto_propina = this.propinaPersonalizada;
        }
        const { data } = await axios.post(
          `${API_URL}/agregar-propina/${idCuenta}`,
          payload,
        );
        // Muestra "Propina aplicada" si se activa, o el mensaje del backend si se desactiva
        if (this.propinaActiva) {
          this.mostrarToastMsg("Propina aplicada", "success");
        } else {
          this.mostrarToastMsg(
            data.message || "Propina actualizada.",
            "success",
          );
        }
        await this.cargarCuenta();
      } catch (err) {
        this.mostrarToastMsg(
          err.response?.data?.message || "Error al actualizar la propina.",
          "danger",
        );
        this.propinaActiva = parseFloat(this.cuenta?.Propina || 0) > 0;
      } finally {
        this.cargando = false;
      }
    },
    async aplicarPropinaPersonalizada() {
      // Solo si la propina está activa y hay un monto ingresado
      if (!this.propinaActiva || this.cuenta?.Estado !== 0) return;
      const monto = this.propinaPersonalizada;
      if (monto === null || monto === "" || isNaN(monto)) return;
      if (parseFloat(monto) <= 0) {
        this.mostrarToastMsg(
          "El monto de propina debe ser mayor a 0.",
          "danger",
        );
        return;
      }
      const idCuenta = this.$route.params.idCuenta;
      try {
        this.cargando = true;
        const { data } = await axios.post(
          `${API_URL}/agregar-propina/${idCuenta}`,
          { propina: 1, monto_propina: parseFloat(monto) },
        );
        this.mostrarToastMsg(
          `Propina actualizada: Q${parseFloat(data.propina).toFixed(2)}`,
          "success",
        );
        await this.cargarCuenta();
      } catch (err) {
        this.mostrarToastMsg(
          err.response?.data?.message || "Error al actualizar la propina.",
          "danger",
        );
      } finally {
        this.cargando = false;
      }
    },
    async limpiarPropinaPersonalizada() {
      this.propinaPersonalizada = null;
      const idCuenta = this.$route.params.idCuenta;
      try {
        this.cargando = true;
        const { data } = await axios.post(
          `${API_URL}/agregar-propina/${idCuenta}`,
          { propina: 1 },
        );
        this.mostrarToastMsg(
          `Propina auto (10%): Q${parseFloat(data.propina).toFixed(2)}`,
          "success",
        );
        await this.cargarCuenta();
      } catch (err) {
        this.mostrarToastMsg(
          err.response?.data?.message || "Error al restablecer la propina.",
          "danger",
        );
      } finally {
        this.cargando = false;
      }
    },
    abrirModalDetalles() {
      this.mostrarModalDetalles = true;
    },
    cerrarModalDetalles() {
      this.mostrarModalDetalles = false;
    },
    reimprimirReciboNormal(pago) {
      const pagoParaImprimir = {
        Monto: pago.Monto,
        metodo_pago: pago.metodo_pago?.Metodo || "N/A",
        NumeroReferencia: pago.NumeroReferencia,
        cuenta_bancaria: pago.cuenta_bancaria
          ? `${pago.cuenta_bancaria.Banco} - ${pago.cuenta_bancaria.NombreCuenta} - ${pago.cuenta_bancaria.NumeroCuenta}`
          : null,
      };
      imprimirReciboRestaurante(pagoParaImprimir, this.cuenta, this.detalles);
    },
    async cargarPagos() {
      const idCuenta = this.$route.params.idCuenta;
      try {
        const { data } = await axios.get(
          `${API_URL}/cuentas-restaurante/${idCuenta}/pagos`,
        );
        this.pagos = Array.isArray(data.pagos) ? data.pagos : [];
      } catch (err) {
        console.error("Error al cargar pagos:", err);
        this.mostrarToastMsg("Error al cargar los pagos", "danger");
      }
    },
    async abrirModalPagos() {
      await this.cargarPagos();
      this.mostrarModalPagos = true;
    },
    cerrarModalPagos() {
      this.mostrarModalPagos = false;
    },
    confirmarAnularPago(pago) {
      this.pagoAAnular = pago;
      this.mostrarConfirmacionAnular = true;
    },
    cancelarAnulacion() {
      this.pagoAAnular = null;
      this.mostrarConfirmacionAnular = false;
    },
    async anularPago() {
      if (!this.pagoAAnular) return;

      try {
        this.cargando = true;
        const { data } = await axios.post(
          `${API_URL}/pagos-restaurante/${this.pagoAAnular.IdPagoCuentaRestaurante}/anular`,
        );

        this.mostrarToastMsg(
          data.message || "Pago cancelado correctamente",
          "success",
        );

        this.cancelarAnulacion();

        // Recargar pagos y cuenta
        await this.cargarPagos();
        await this.cargarCuenta();
      } catch (err) {
        console.error(err);
        this.mostrarToastMsg(
          err.response?.data?.message || "Error al cancelar el pago",
          "danger",
        );
      } finally {
        this.cargando = false;
      }
    },
    abrirModalReemitir(pago) {
      this.reemitirPago = pago;
      this.reemitirNit = "";
      this.reemitirNombre = "";
      this.mostrarModalReemitir = true;
    },
    cerrarModalReemitir() {
      this.mostrarModalReemitir = false;
      this.reemitirPago = null;
    },
    async reemitirFactura() {
      if (!this.reemitirPago) return;
      try {
        this.cargandoReemitir = true;
        const { data } = await axios.post(
          `${API_URL}/pagos-restaurante/${this.reemitirPago.IdPagoCuentaRestaurante}/reemitir`,
          {
            receptor_nit: this.reemitirNit.trim() || "CF",
            receptor_nombre: this.reemitirNombre.trim() || "Consumidor Final",
          },
        );
        this.cerrarModalReemitir();
        this.mostrarToastMsg("✔ Factura generada correctamente", "success");
        if (data.factura?.uuid) {
          setTimeout(() => {
            const url = `${API_URL}/facturas-fel/${data.factura.uuid}/carta`;
            window.open(url, "_blank");
          }, 400);
        }
        await this.cargarPagos();
      } catch (err) {
        this.mostrarToastMsg(
          "⚠ Error: " + (err.response?.data?.message || "No se pudo generar la factura"),
          "danger",
        );
      } finally {
        this.cargandoReemitir = false;
      }
    },
    mostrarToastMsg(msg, tipo) {
      this.mensajeToast = msg;
      this.tipoToast = tipo;
      this.mostrarToast = true;
      setTimeout(() => (this.mostrarToast = false), 3000);
    },
    cerrarToast() {
      this.mostrarToast = false;
    },
    // Métodos para código de descuento
    abrirModalDescuento() {
      this.codigoDescuento = "";
      this.mostrarModalDescuento = true;
    },
    cerrarModalDescuento() {
      this.mostrarModalDescuento = false;
      this.codigoDescuento = "";
    },
    async aplicarDescuento() {
      if (!this.codigoDescuento) {
        return this.mostrarToastMsg("Ingrese un código de descuento", "danger");
      }

      try {
        this.cargandoDescuento = true;

        const idCuentaPedido = this.cuenta.IdCuentaPedido;
        const { data } = await axios.post(
          `${API_URL}/cuentas-restaurante/${idCuentaPedido}/aplicar-descuento`,
          { codigo: this.codigoDescuento },
        );

        this.mostrarToastMsg(
          `${data.message} - Monto descontado: Q${parseFloat(
            data.montoDescuento,
          ).toFixed(2)}`,
          "success",
        );

        this.cerrarModalDescuento();

        // Recargar datos para actualizar saldo
        await this.cargarCuenta();
      } catch (error) {
        console.error("Error al aplicar descuento:", error);
        this.mostrarToastMsg(
          error.response?.data?.message || "Error al aplicar el descuento",
          "danger",
        );
      } finally {
        this.cargandoDescuento = false;
      }
    },
    async confirmarQuitarDescuentoCuenta() {
      const idCuentaPedido = this.cuenta.IdCuentaPedido;
      try {
        this.cargandoQuitarDescuento = true;
        const { data } = await axios.post(
          `${API_URL}/cuentas-restaurante/${idCuentaPedido}/quitar-descuento`,
        );

        this.mostrarToastMsg(
          data.message ||
            `Descuento revertido: Q${parseFloat(data.montoRevertido || 0).toFixed(2)}`,
          "success",
        );

        this.modalQuitarDescuentoVisible = false;
        await this.cargarCuenta();
      } catch (error) {
        console.error("Error al quitar el descuento:", error);
        this.mostrarToastMsg(
          error.response?.data?.message || "Error al quitar el descuento",
          "danger",
        );
      } finally {
        this.cargandoQuitarDescuento = false;
      }
    },
    async abrirModalUnificaciones() {
      this.mostrarModalUnificaciones = true;
      this.cargandoUnificaciones = true;
      try {
        const idCuenta = this.$route.params.idCuenta;
        const { data } = await axios.get(`${API_URL}/cuentas-restaurante/${idCuenta}/unificaciones`);
        this.unificaciones = Array.isArray(data.data) ? data.data : [];
      } catch {
        this.mostrarToastMsg('Error al cargar unificaciones', 'danger');
      } finally {
        this.cargandoUnificaciones = false;
      }
    },
    async deshacerUnificacion(unificacion) {
      if (!confirm(`¿Deshacer la absorción de "${unificacion.cuenta_origen?.NombreCuenta}"?`)) return;
      try {
        this.cargandoDeshacerUnificacion = unificacion.IdUnificacion;
        const { data } = await axios.post(`${API_URL}/unificaciones/${unificacion.IdUnificacion}/deshacer`);
        this.mostrarToastMsg(data.message || 'Unificación deshecha correctamente', 'success');
        await this.abrirModalUnificaciones();
        await this.cargarCuenta();
      } catch (err) {
        this.mostrarToastMsg(
          err.response?.data?.message || 'Error al deshacer la unificación',
          'danger',
        );
      } finally {
        this.cargandoDeshacerUnificacion = null;
      }
    },
    abrirModalCambiarHabitacion() {
      this.cambiarBusquedaReserva = "";
      this.cambiarReservasFiltradas = [];
      this.cambiarHabitacionSeleccionada = null;
      this.mostrarModalCambiarHabitacion = true;
    },
    cerrarModalCambiarHabitacion() {
      this.mostrarModalCambiarHabitacion = false;
      this.cambiarBusquedaReserva = "";
      this.cambiarReservasFiltradas = [];
      this.cambiarHabitacionSeleccionada = null;
    },
    async buscarReservasCambio() {
      if (this.cambiarBusquedaReserva.length < 2) {
        this.cambiarReservasFiltradas = [];
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/reservas/buscar-avanzado`, {
          params: { q: this.cambiarBusquedaReserva, solo_checkin_activo: 1 },
        });
        this.cambiarReservasFiltradas = Array.isArray(res.data.data) ? res.data.data : [];
      } catch {
        this.cambiarReservasFiltradas = [];
      }
    },
    seleccionarHabitacionCambio(reserva, habitacion) {
      this.cambiarHabitacionSeleccionada = habitacion;
      this.cambiarBusquedaReserva = `R00${reserva.IdReserva} - Habitación ${habitacion.Numero}`;
      this.cambiarReservasFiltradas = [];
    },
    async confirmarCambioHabitacion() {
      if (!this.cambiarHabitacionSeleccionada) return;
      try {
        this.cargandoCambioHabitacion = true;
        const idCuenta = this.$route.params.idCuenta;
        const { data } = await axios.put(
          `${API_URL}/pagar-cuenta-pedido/${idCuenta}/cargar-habitacion`,
          { id_reserva_habitacion: this.cambiarHabitacionSeleccionada.IdReservaHabitacion },
        );
        this.mostrarToastMsg(data.message || "Habitación cambiada correctamente", "success");
        this.cerrarModalCambiarHabitacion();
        await this.cargarCuenta();
      } catch (err) {
        this.mostrarToastMsg(
          err.response?.data?.message || "Error al cambiar la habitación",
          "danger",
        );
      } finally {
        this.cargandoCambioHabitacion = false;
      }
    },
    // Navegar al paso intermedio (normal vs crédito) antes de sin-factura
    avanzarASinFactura() {
      this.pasoModalFactura = 3;
    },
    seleccionarPagoCredito() {
      this.mostrarModalFactura = false;
      this.pasoModalFactura = 1;
      this.creditoNombre = "";
      this.creditoNit = "";
      this.creditoDescripcion = `Consumo de restaurante - ${this.cuenta?.NombreCuenta || ""}`;
      this.creditoFechaVencimiento = "";
      this.mostrarModalCredito = true;
    },
    cerrarModalCredito() {
      this.mostrarModalCredito = false;
      this.creditoNombre = "";
      this.creditoNit = "";
      this.creditoDescripcion = "";
      this.creditoFechaVencimiento = "";
    },
    async procesarPagoCredito() {
      if (!this.creditoFechaVencimiento) {
        return this.mostrarToastMsg("Ingrese una fecha de vencimiento", "danger");
      }

      const idCuenta = this.$route.params.idCuenta;
      const monto = parseFloat(
        this.cuenta?.SaldoPendienteCuenta || this.cuenta?.Total || 0
      );
      const idEmpleado = this.$store.state.user?.IdEmpleado;

      try {
        this.cargandoCredito = true;

        // 1. Registrar el pago normal (sin factura, método efectivo por defecto)
        const { data: metodos } = await axios.get(`${API_URL}/metodos-pago/no-facturados`);
        if (!Array.isArray(metodos) || metodos.length === 0) {
          return this.mostrarToastMsg("No hay métodos de pago sin factura configurados. Contacte al administrador.", "danger");
        }
        const primerMetodo = metodos[0].IdMetodoPago;

        const { data: pagoData } = await axios.post(
          `${API_URL}/cuentas-restaurante/${idCuenta}/pagar`,
          {
            Monto: monto,
            IdMetodoPago: primerMetodo,
            receptor_nit: this.creditoNit.trim() || "CF",
            receptor_nombre: this.creditoNombre.trim() || "Consumidor Final",
            generar_factura: false,
          },
        );

        // 2. Registrar el crédito referenciando el pago creado
        //    Si este paso falla, la cuenta ya fue marcada como pagada en paso 1.
        //    El administrador puede crear el crédito manualmente con el ID del pago.
        try {
          await axios.post(`${API_URL}/pagos-credito`, {
            IdPagoCuentaRestaurante: pagoData.pago?.IdPagoCuentaRestaurante ?? null,
            IdCuentaPedido: parseInt(idCuenta),
            Monto: monto,
            FechaVencimiento: this.creditoFechaVencimiento,
            IdEmpleado: idEmpleado,
            ReceptorNIT: this.creditoNit.trim() || null,
            ReceptorNombre: this.creditoNombre.trim() || null,
            Descripcion: this.creditoDescripcion.trim() || null,
          });
        } catch (errCredito) {
          console.error("[Credit step 2 failed]", errCredito);
          this.mostrarToastMsg(
            `⚠️ El pago fue registrado (ID: ${pagoData.pago?.IdPagoCuentaRestaurante ?? "N/A"}) pero no se pudo crear el registro de crédito. ` +
            `Anote este ID y comunique al administrador para completar el proceso.`,
            "danger",
          );
          await this.cargarCuenta();
          await this.cargarPagos();
          return;
        }

        this.mostrarToastMsg("Pago a crédito registrado correctamente", "success");
        this.cerrarModalCredito();
        await this.cargarCuenta();
        await this.cargarPagos();
      } catch (err) {
        this.mostrarToastMsg(
          err.response?.data?.message || "Error al registrar el pago a crédito",
          "danger",
        );
      } finally {
        this.cargandoCredito = false;
      }
    },
  },
  created() {
    this.debouncedBuscarReservas = debounce(this.buscarReservas, 400);
    this.debouncedBuscarReservasCambio = debounce(this.buscarReservasCambio, 400);
  },
  mounted() {
    this.cargarCuenta();
  },
};
</script>

<style scoped>
.table th,
.table td {
  vertical-align: middle;
  text-align: center;
}
.btn {
  min-width: 120px;
}
.toast {
  border-radius: 12px;
}
.toast-body {
  font-size: 0.9rem;
}
.modal {
  display: none;
}
.modal.show {
  display: block;
}
/* Switch estilo iOS */
.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  vertical-align: middle;
}
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 24px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}
input:checked + .slider {
  background-color: #198754;
}
input:checked + .slider:before {
  transform: translateX(20px);
}
</style>
